import { 
    AssessmentDefinition,
    App, 
    ClientActivityPoint,
    ClientActivityState,
    Client,
    ClientBriefingState,
    ClientContentState,
    ClientsState, 
    CommsCampaign,
    CommsCampaignsState,
    Condition, 
    ConditionsState, 
    Family,
    FamilySponsorshipStatus,
    HealthcareProviderInfo, 
    Nullable, 
    SessionRecordingClient,
    SpeechNeed,
    SpeechNeedsState,
    Therapist, 
    TherapySession,
    TherapySessionPlacedTag,
    TherapySessionReport,
    TherapySessionReportGenerationRequest,
    TherapySessionReportsState,
    TherapySessionTagDefinition,
    TherapySessionTranscriptUtterance,
    TherapySessionsState,
    TimelineFileUploadOptions,
    TimelineDocument,
    TimelineDocumentsState,
    TherapistsState,
    User, 
    UserResponseRecord,
} from '../interfaces';
import { getFunctions, httpsCallable } from 'firebase/functions';

import { 
    PresentedData, 
    PresentedView, 
    Presenter, 
} from './Presentation';
import { ClientsImportEntry, ClientsImportResult } from './ClientsImport';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { getDashboardRole, hasDashboardFeature, isRootOrSuperRole } from '../DashboardAccess';
import { countFamiliesUsingApp, getClientActivityIdentifiers } from '../familyAppUsage';

type ImportCarerReuseContext = {
    carerByEmailKey: Map<string, Client>;
    carerByPhoneKey: Map<string, Client>;
};

type CarerResolution = {
    carer: Client;
    wasCreated: boolean;
};

export class ClientsPresenter implements Presenter {
    private app: App;
    private view: Nullable<PresentedView>;
    private allClients: Array<Client> = [];
    private clients: Array<Client> = [];
    private selectedClient: Nullable<Client> = null;
    private therapists: Array<Therapist> = [];
    private currentTherapist: Nullable<Therapist> = null;
    private commsCampaigns: Array<CommsCampaign> = [];
    private activityByClientId: Record<string, Array<number>> = {};
    private activityTimelineByClientId: Record<string, Array<ClientActivityPoint>> = {};
    private sessionMinutesTimelineByClientId: Record<string, Array<ClientActivityPoint>> = {};
    private signUpTimeByClientId: Record<string, string> = {};
    private stageByClientId: Record<string, string> = {};
    private mobileAppUsageByClientId: Record<string, boolean> = {};
    private mobileAppUsageStatus: NonNullable<ClientActivityState['mobileAppUsageStatus']> = 'loading';
    private assessmentDefinitions: Array<AssessmentDefinition> = [];
    private selectedClientAssessmentResponses: Array<UserResponseRecord> = [];
    private isLoadingSelectedClientAssessments: boolean = false;
    private speechNeeds: Array<SpeechNeed> = [];
    private conditions: Array<Condition> = [];
    private healthcareProviderInfo: HealthcareProviderInfo = {
        id: '',
        name: '',
        dashboardUrl: '',
        inviteCodes: [],
        reportTemplates: [],
    };
    private authenticatedUser?: User;
    private selectedClientAssessmentsRequestId: number = 0;
    private selectedClientSponsorshipRequestId: number = 0;
    private selectedClientSponsorship: FamilySponsorshipStatus | null = null;
    private isLoadingSelectedClientSponsorship: boolean = false;
    private selectedClientSponsorshipError: string = '';
    private isLoadingAssessmentDefinitions: boolean = false;
    private therapySessions: Array<TherapySession> = [];
    private therapySessionReports: Array<TherapySessionReport> = [];
    private timelineDocuments: Array<TimelineDocument> = [];
    private onClientRecordChange: (client: Nullable<Client>) => void;
    private onImportSessionFile: (
        client: SessionRecordingClient,
        file: File,
        options?: TimelineFileUploadOptions
    ) => Promise<string>;

    constructor(options: {
        app: App,
        onClientRecordChange?: (client: Nullable<Client>) => void,
        onImportSessionFile?: (
            client: SessionRecordingClient,
            file: File,
            options?: TimelineFileUploadOptions
        ) => Promise<string>,
    }) {
        const { app, onImportSessionFile, onClientRecordChange } = options;
        if (!app) {
            throw new Error('ClientsPresenter(): missing app parameter');
        }
        this.app = app;
        this.onClientRecordChange = onClientRecordChange || (() => {});
        this.onImportSessionFile = onImportSessionFile || (async () => { throw new Error('Media import is unavailable.'); });
        this.observeAuthenticatedUser();
        this.observeClients();
        this.observeTherapySessions();
        this.observeTherapySessionReports();
        this.observeTimelineDocuments();
        this.observeTherapists();
        this.observeCurrentTherapist();
        this.observeCommsCampaigns();
        this.observeClientActivity();
        this.observeSpeechNeeds();
        this.observeConditions();
        this.observeHealthcareProviderInfo();
    }

    private observeAuthenticatedUser() {
        this.app.queryUser().observe({
            changed: (user: User) => {
                this.authenticatedUser = user;
                if (this.canUseClients()) {
                    void this.loadAssessmentDefinitions();
                } else {
                    this.clearSelectedClientState();
                }
                this.safeUpdatePresentedData();
            }
        });
    }
    
    private observeClients() {
        this.app.getData().queryClients().observe({
            changed: (clients: ClientsState) => {
                this.allClients = clients.allClients;
                this.clients = clients.clients;
                this.safeUpdatePresentedData();
            }
        });
    }

    // Keep client records supplied with the provider-scoped session list.
    private observeTherapySessions() {
        this.app.getData().queryTherapySessions().observe({
            changed: (sessionsState: TherapySessionsState) => {
                this.therapySessions = sessionsState.sessions;
                this.safeUpdatePresentedData();
            }
        });
    }

    // Keep client records supplied with generated report documents.
    private observeTherapySessionReports() {
        this.app.getData().queryTherapySessionReports().observe({
            changed: (reportsState: TherapySessionReportsState) => {
                this.therapySessionReports = reportsState.reports;
                this.safeUpdatePresentedData();
            }
        });
    }

    // Keep client records supplied with non-audio timeline documents.
    private observeTimelineDocuments() {
        this.app.getData().queryTimelineDocuments().observe({
            changed: (timelineDocumentsState: TimelineDocumentsState) => {
                this.timelineDocuments = timelineDocumentsState.documents;
                this.safeUpdatePresentedData();
            }
        });
    }

    private observeTherapists() {
        this.app.getData().queryTherapistsInCurrentHealthcareProvider().observe({
            changed: (therapists: TherapistsState) => {
                this.therapists = therapists.therapists;
                this.safeUpdatePresentedData();
            }
        });
    }

    private observeCurrentTherapist() {
        this.app.getData().queryCurrentTherapist().observe({
            changed: (therapist: Therapist) => {
                this.currentTherapist = therapist?.id ? therapist : null;
                this.safeUpdatePresentedData();
            }
        });
    }

    private observeCommsCampaigns() {
        this.app.getData().queryCommsCampaigns().observe({
            changed: (campaignsState: CommsCampaignsState) => {
                this.commsCampaigns = campaignsState.campaigns;
                this.safeUpdatePresentedData();
            }
        });
    }

    private observeClientActivity() {
        this.app.getData().queryClientActivity().observe({
            changed: (activityState: ClientActivityState) => {
                this.activityByClientId = activityState.activityByClientId;
                this.activityTimelineByClientId = activityState.activityTimelineByClientId;
                this.sessionMinutesTimelineByClientId = activityState.sessionMinutesTimelineByClientId;
                this.signUpTimeByClientId = activityState.signUpTimeByClientId;
                this.stageByClientId = activityState.stageByClientId;
                this.mobileAppUsageByClientId = activityState.mobileAppUsageByClientId || {};
                this.mobileAppUsageStatus = activityState.mobileAppUsageStatus || 'loading';
                this.safeUpdatePresentedData();
            }
        });
    }

    private observeSpeechNeeds() {
        this.app.getData().querySpeechNeeds().observe({
            changed: (speechNeeds: SpeechNeedsState) => {
                this.speechNeeds = speechNeeds.speechNeeds;
                this.safeUpdatePresentedData();
            }
        });
    }

    private observeConditions() {
        this.app.getData().queryConditions().observe({
            changed: (conditions: ConditionsState) => {
                this.conditions = conditions.conditions;
                this.safeUpdatePresentedData();
            }
        });
    }

    private observeHealthcareProviderInfo() {
        this.app.getData().queryHealthcareProviderInfo().observe({
            changed: (healthcareProviderInfo: HealthcareProviderInfo) => {
                const providerChanged = this.healthcareProviderInfo.id !== healthcareProviderInfo.id;
                this.healthcareProviderInfo = healthcareProviderInfo;
                if (providerChanged || !this.canUseClients()) {
                    this.clearSelectedClientState();
                }
                if (this.canUseClients()) {
                    void this.loadAssessmentDefinitions();
                }
                this.safeUpdatePresentedData();
            }
        });
    }

    private canUseClients(): boolean {
        const role = this.authenticatedUser?.dashboardRole || getDashboardRole(this.currentTherapist);
        return !!this.healthcareProviderInfo.id &&
            hasDashboardFeature(this.healthcareProviderInfo, role, 'clients');
    }

    private requireClientsAccess() {
        // Repeat the Clients entitlement at every record callback for stale views.
        if (!this.canUseClients()) {
            throw new Error('Client records are not available for your role in this healthcare provider.');
        }
    }

    // Client plans require explicit provider availability as well as Clients access.
    private canUseClientPlans(): boolean {
        const role = this.authenticatedUser?.dashboardRole || getDashboardRole(this.currentTherapist);
        return this.canUseClients() &&
            hasDashboardFeature(this.healthcareProviderInfo, role, 'client_plans');
    }

    // Recheck live entitlements so callbacks retained by an open editor cannot bypass revocation.
    private requireClientPlansAccess() {
        if (!this.canUseClientPlans()) {
            throw new Error('Client plans are not available for your role in this healthcare provider.');
        }
    }

    private clearSelectedClientState() {
        // Drop record details immediately on provider changes or entitlement loss.
        this.selectedClientAssessmentsRequestId += 1;
        this.selectedClientSponsorshipRequestId += 1;
        this.selectedClient = null;
        this.selectedClientAssessmentResponses = [];
        this.isLoadingSelectedClientAssessments = false;
        this.selectedClientSponsorship = null;
        this.isLoadingSelectedClientSponsorship = false;
        this.selectedClientSponsorshipError = '';
        this.assessmentDefinitions = [];
    }

    private isSelfServiceCareRecipient(client: Client): boolean {
        return this.healthcareProviderInfo.commercialModel === 'self_service' &&
            client.userType === 'care_recipient' && !!client.id;
    }

    private canManageFamilySponsorship(): boolean {
        const role = this.authenticatedUser?.dashboardRole || getDashboardRole(this.currentTherapist);
        return this.healthcareProviderInfo.commercialModel === 'self_service' &&
            (role === 'admin' || role === 'root' || role === 'super');
    }

    // Resolve audio/report access from the current professional and provider matrix.
    private canUseAudioScribeAndReports(): boolean {
        const role = this.authenticatedUser?.dashboardRole || getDashboardRole(this.currentTherapist);
        return this.canUseClients() &&
            hasDashboardFeature(this.healthcareProviderInfo, role, 'audio_scribe_reports');
    }

    // Guard presenter callbacks as well as hiding their controls in the view.
    private requireAudioScribeAndReportsAccess() {
        if (!this.canUseAudioScribeAndReports()) {
            throw new Error(
                'Audio scribe and report features are not available for your role in this healthcare provider.'
            );
        }
    }

    private setSelectedClient(client: Client) {
        this.requireClientsAccess();
        this.selectedClient = client;
        this.selectedClientAssessmentResponses = [];
        this.isLoadingSelectedClientAssessments = true;
        this.selectedClientSponsorship = null;
        this.selectedClientSponsorshipError = '';
        this.safeUpdatePresentedData();
        void this.loadSelectedClientAssessments(client);
        void this.loadSelectedClientSponsorship(client);
    }

    // Load the server-owned current sponsorship separately from historical license usage.
    private async loadSelectedClientSponsorship(client: Client): Promise<void> {
        const requestId = ++this.selectedClientSponsorshipRequestId;
        if (!this.isSelfServiceCareRecipient(client)) {
            this.isLoadingSelectedClientSponsorship = false;
            this.selectedClientSponsorship = null;
            this.safeUpdatePresentedData();
            return;
        }

        this.isLoadingSelectedClientSponsorship = true;
        this.selectedClientSponsorshipError = '';
        this.safeUpdatePresentedData();
        try {
            const getStatus = httpsCallable<
                { healthcareProviderId: string; careRecipientId: string },
                FamilySponsorshipStatus
            >(getFunctions(), 'getFamilySponsorshipStatus');
            const result = await getStatus({
                healthcareProviderId: this.healthcareProviderInfo.id,
                careRecipientId: client.id!,
            });
            if (requestId !== this.selectedClientSponsorshipRequestId || this.selectedClient?.id !== client.id) {
                return;
            }
            this.selectedClientSponsorship = result.data;
        } catch (error) {
            if (requestId !== this.selectedClientSponsorshipRequestId || this.selectedClient?.id !== client.id) {
                return;
            }
            console.error('CLIENTS_PRESENTER: loadSelectedClientSponsorship():', error);
            this.selectedClientSponsorshipError = error instanceof Error ? error.message :
                'Could not load sponsored mobile access.';
        } finally {
            if (requestId === this.selectedClientSponsorshipRequestId && this.selectedClient?.id === client.id) {
                this.isLoadingSelectedClientSponsorship = false;
                this.safeUpdatePresentedData();
            }
        }
    }

    // Grant or revoke sponsorship without changing the carer's login or personal subscription.
    private async setSelectedClientSponsorship(sponsored: boolean): Promise<void> {
        const client = this.selectedClient;
        if (!client || !this.isSelfServiceCareRecipient(client) || !this.canManageFamilySponsorship()) {
            throw new Error('Only an Admin can manage sponsored mobile access for this family.');
        }

        const requestId = ++this.selectedClientSponsorshipRequestId;
        this.isLoadingSelectedClientSponsorship = true;
        this.selectedClientSponsorshipError = '';
        this.safeUpdatePresentedData();
        try {
            const setSponsorship = httpsCallable<
                { healthcareProviderId: string; careRecipientId: string; sponsored: boolean },
                Pick<FamilySponsorshipStatus, 'providerId' | 'careRecipientId' | 'sponsored' | 'activeCarerIds'>
            >(getFunctions(), 'setFamilySponsorship');
            const result = await setSponsorship({
                healthcareProviderId: this.healthcareProviderInfo.id,
                careRecipientId: client.id!,
                sponsored,
            });
            if (requestId !== this.selectedClientSponsorshipRequestId || this.selectedClient?.id !== client.id) {
                return;
            }
            this.selectedClientSponsorship = {
                ...(this.selectedClientSponsorship || {
                    providerId: result.data.providerId,
                    careRecipientId: result.data.careRecipientId,
                    everAllocated: sponsored,
                }),
                ...result.data,
            };
            // Refresh the signed usage and overage banners after the server transaction commits.
            this.app.getData().setHealthcareProviderId(this.healthcareProviderInfo.id);
        } catch (error) {
            if (requestId !== this.selectedClientSponsorshipRequestId || this.selectedClient?.id !== client.id) {
                return;
            }
            console.error('CLIENTS_PRESENTER: setSelectedClientSponsorship():', error);
            this.selectedClientSponsorshipError = error instanceof Error ? error.message :
                'Could not update sponsored mobile access.';
        } finally {
            if (requestId === this.selectedClientSponsorshipRequestId && this.selectedClient?.id === client.id) {
                this.isLoadingSelectedClientSponsorship = false;
                this.safeUpdatePresentedData();
            }
        }
    }

    private async loadAssessmentDefinitions() {
        if (!this.canUseClients() || this.isLoadingAssessmentDefinitions || this.assessmentDefinitions.length > 0) {
            return;
        }

        this.isLoadingAssessmentDefinitions = true;
        try {
            this.assessmentDefinitions = await this.app.getData().fetchAssessments();
        } catch (error) {
            console.error('CLIENTS_PRESENTER: loadAssessmentDefinitions():', error);
            this.assessmentDefinitions = [];
        } finally {
            this.isLoadingAssessmentDefinitions = false;
        }
        this.safeUpdatePresentedData();
    }

    private async loadSelectedClientAssessments(client: Client) {
        this.requireClientsAccess();
        const requestId = ++this.selectedClientAssessmentsRequestId;

        try {
            const responses = await this.app.getData().fetchUserResponsesForClient(client);
            if (requestId !== this.selectedClientAssessmentsRequestId || this.selectedClient?.id !== client.id) {
                return;
            }

            this.selectedClientAssessmentResponses = responses;
        } catch (error) {
            console.error('CLIENTS_PRESENTER: loadSelectedClientAssessments():', error);
            if (requestId !== this.selectedClientAssessmentsRequestId || this.selectedClient?.id !== client.id) {
                return;
            }

            this.selectedClientAssessmentResponses = [];
        }

        if (requestId !== this.selectedClientAssessmentsRequestId || this.selectedClient?.id !== client.id) {
            return;
        }

        this.isLoadingSelectedClientAssessments = false;
        this.safeUpdatePresentedData();
    }

    private formatClientName(client: Client): string {
        if (client.givenName && client.familyName) {
            return `${client.familyName}, ${client.givenName}`;
        }
        return client.givenName || client.displayName || '';
    }

    private prepareClientForCreation(client: Client): Client {
        const preparedClient: Client = {
            ...client,
            providerId: this.healthcareProviderInfo.id,
            providerName: this.healthcareProviderInfo.name,
        };

        if (this.authenticatedUser?.authenticatedUser?.uid) {
            preparedClient.therapistUserId = this.authenticatedUser.authenticatedUser.uid;
        }

        return preparedClient;
    }

    private prepareCarerForCreation(carer: Client): Client {
        const preparedCarer: Client = {
            ...carer,
            providerId: this.healthcareProviderInfo.id,
            providerName: this.healthcareProviderInfo.name,
        };

        if (this.authenticatedUser?.authenticatedUser?.uid) {
            preparedCarer.therapistUserId = this.authenticatedUser.authenticatedUser.uid;
        }

        return preparedCarer;
    }

    // Normalize names so importer carer matching is case- and whitespace-insensitive.
    private normalizeClientNamePart(value?: string): string {
        return (value || '').trim().toLowerCase();
    }

    // Normalize emails before matching imported carers to existing records.
    private normalizeClientEmail(value?: string): string {
        return (value || '').trim().toLowerCase();
    }

    // Normalize phone numbers to E.164 where possible before matching carers.
    private normalizeClientPhoneNumber(value?: string): string {
        const trimmedPhoneNumber = (value || '').trim();
        if (!trimmedPhoneNumber) {
            return '';
        }

        const parsedPhoneNumber = parsePhoneNumberFromString(trimmedPhoneNumber, 'GB');
        return parsedPhoneNumber ? parsedPhoneNumber.format('E.164') : trimmedPhoneNumber;
    }

    private buildCarerPhoneKey(carer: Pick<Client, 'givenName' | 'familyName' | 'phoneNumber'>): string | null {
        const givenName = this.normalizeClientNamePart(carer.givenName);
        const familyName = this.normalizeClientNamePart(carer.familyName);
        const phoneNumber = this.normalizeClientPhoneNumber(carer.phoneNumber);
        if (!givenName || !familyName || !phoneNumber) {
            return null;
        }

        return `${givenName}::${familyName}::phone::${phoneNumber}`;
    }

    private buildCarerEmailKey(carer: Pick<Client, 'givenName' | 'familyName' | 'email'>): string | null {
        const givenName = this.normalizeClientNamePart(carer.givenName);
        const familyName = this.normalizeClientNamePart(carer.familyName);
        const email = this.normalizeClientEmail(carer.email);
        if (!givenName || !familyName || !email) {
            return null;
        }

        return `${givenName}::${familyName}::email::${email}`;
    }

    private addCarerToReuseContext(context: ImportCarerReuseContext, carer: Client) {
        const phoneKey = this.buildCarerPhoneKey(carer);
        if (phoneKey) {
            context.carerByPhoneKey.set(phoneKey, carer);
        }

        const emailKey = this.buildCarerEmailKey(carer);
        if (emailKey) {
            context.carerByEmailKey.set(emailKey, carer);
        }
    }

    private buildImportCarerReuseContext(): ImportCarerReuseContext {
        const context: ImportCarerReuseContext = {
            carerByEmailKey: new Map<string, Client>(),
            carerByPhoneKey: new Map<string, Client>(),
        };

        this.clients
            .filter((client) => client.userType === 'carer')
            .forEach((carer) => this.addCarerToReuseContext(context, carer));

        return context;
    }

    private async resolveImportCarer(
        preparedCarer: Client,
        reuseContext?: ImportCarerReuseContext
    ): Promise<CarerResolution> {
        if (!reuseContext) {
            const createdCarerId = await this.app.getData().addClient(preparedCarer);
            return {
                carer: {
                    ...preparedCarer,
                    id: createdCarerId,
                },
                wasCreated: true,
            };
        }

        const phoneKey = this.buildCarerPhoneKey(preparedCarer);
        const emailKey = this.buildCarerEmailKey(preparedCarer);
        const matchingCarerByPhone = phoneKey ? reuseContext.carerByPhoneKey.get(phoneKey) : null;
        const matchingCarerByEmail = emailKey ? reuseContext.carerByEmailKey.get(emailKey) : null;

        if (matchingCarerByPhone && matchingCarerByEmail && matchingCarerByPhone.id !== matchingCarerByEmail.id) {
            throw new Error(
                `Found conflicting existing carers for ${preparedCarer.givenName} ${preparedCarer.familyName}. ` +
                'One matches the phone number and another matches the email address.'
            );
        }

        const matchingCarer = matchingCarerByPhone || matchingCarerByEmail;
        if (matchingCarer?.id) {
            return {
                carer: matchingCarer,
                wasCreated: false,
            };
        }

        const createdCarerId = await this.app.getData().addClient(preparedCarer);
        const createdCarer = {
            ...preparedCarer,
            id: createdCarerId,
        };
        this.addCarerToReuseContext(reuseContext, createdCarer);

        return {
            carer: createdCarer,
            wasCreated: true,
        };
    }

    private async createClientWithCarers(
        client: Client,
        carers: Client[],
        reuseContext?: ImportCarerReuseContext
    ): Promise<{ createdCarers: number; reusedCarers: number }> {
        this.requireClientsAccess();
        const preparedClient = this.prepareClientForCreation(client);
        const preparedCarers = carers.map(carer => this.prepareCarerForCreation(carer));

        console.log('CLIENTS_PRESENTER: createClientWithCarers:', preparedClient, preparedCarers);

        const resolvedCarers = await Promise.all(
            preparedCarers.map(async (carer) => this.resolveImportCarer(carer, reuseContext))
        );
        const createdCarers = resolvedCarers.filter((resolution) => resolution.wasCreated).length;
        const reusedCarers = resolvedCarers.length - createdCarers;
        const resolvedCarerClients = resolvedCarers.map((resolution) => resolution.carer);
        const carerIds = resolvedCarerClients
            .map((carer) => carer.id)
            .filter((carerId): carerId is string => typeof carerId === 'string' && carerId.trim() !== '');

        if (carerIds.length > 0) {
            preparedClient.carers = carerIds;
        }

        const clientId = await this.app.getData().addClient(preparedClient);
        preparedClient.id = clientId;

        if (resolvedCarerClients.length > 0) {
            const familyMembers = resolvedCarerClients.map((carer, index) => ({
                displayName: this.formatClientName(carer),
                userId: carerIds[index],
            }));

            const family: Family = {
                careRecipient: clientId,
                members: familyMembers,
                createdAt: new Date(),
            };

            await this.app.getData().addFamily(family);
        }

        const newCarerClients = resolvedCarerClients.filter((carer) =>
            !this.clients.some((existingClient) => existingClient.id === carer.id)
        );
        this.clients = [...this.clients, ...newCarerClients, preparedClient];
        this.allClients = [...this.allClients, ...newCarerClients, preparedClient];
        this.safeUpdatePresentedData();

        return {
            createdCarers,
            reusedCarers,
        };
    }

    private addClient(client: Client, carers: Client[]) {
        void this.createClientWithCarers(client, carers).catch((error) => {
            console.error('CLIENTS_PRESENTER: addClient(): Error adding client:', error);
            window.alert(error instanceof Error ? error.message : 'Failed to create client.');
        });
    }

    private async importClients(entries: ClientsImportEntry[]): Promise<ClientsImportResult> {
        this.requireClientsAccess();
        let importedRows = 0;
        let importedCarers = 0;
        let reusedCarers = 0;
        const reuseContext = this.buildImportCarerReuseContext();

        for (const entry of entries) {
            try {
                const importResult = await this.createClientWithCarers(entry.client, entry.carers, reuseContext);
                importedRows += 1;
                importedCarers += importResult.createdCarers;
                reusedCarers += importResult.reusedCarers;
            } catch (error) {
                console.error(`CLIENTS_PRESENTER: importClients(): Error importing row ${entry.rowNumber}:`, error);
                const baseMessage = error instanceof Error ? error.message : 'Unknown import error';
                throw new Error(`Import failed on CSV row ${entry.rowNumber}. ${baseMessage}`);
            }
        }

        if (this.healthcareProviderInfo.id) {
            this.app.getData().setHealthcareProviderId(this.healthcareProviderInfo.id);
        }

        return {
            importedRows,
            importedClients: importedRows,
            importedCarers,
            reusedCarers,
        };
    }

    presentedData(): PresentedData {
        // Referral creation alone does not consume a family's first-app-use licence.
        const usedLicenses = this.mobileAppUsageStatus === 'ready' ?
            countFamiliesUsingApp(this.clients, this.mobileAppUsageByClientId) : undefined;
        const canUseAudioScribeAndReports = this.canUseAudioScribeAndReports();

        return {
            authenticatedUserType: this.authenticatedUser?.type || 'therapist',
            authenticatedUserId: this.authenticatedUser?.authenticatedUser?.uid || '',
            usedLicenses,
            mobileAppUsageStatus: this.mobileAppUsageStatus,
            // Auth dates include dashboard use; only mobile activity establishes app use here.
            hasUsedTherapyApp: !!this.selectedClient && getClientActivityIdentifiers(this.selectedClient)
                .some((id) => this.mobileAppUsageByClientId[id] === true),
            totalLicenses: this.healthcareProviderInfo.licenses,
            licenseUsage: this.healthcareProviderInfo.commercialModel === 'self_service' ?
                this.healthcareProviderInfo.licenseUsage : undefined,
            allClients: this.allClients,
            clients: this.clients,
            activityByClientId: this.activityByClientId,
            activityTimelineByClientId: this.activityTimelineByClientId,
            sessionMinutesTimelineByClientId: this.sessionMinutesTimelineByClientId,
            signUpTimeByClientId: this.signUpTimeByClientId,
            stageByClientId: this.stageByClientId,
            therapists: this.therapists,
            currentTherapist: this.currentTherapist,
            commsCampaignIds: this.commsCampaigns.map(campaign => campaign.id),
            defaultCommsCampaignId: this.commsCampaigns.find(campaign => campaign.name === 'default')?.id,
            reportTemplates: this.healthcareProviderInfo.reportTemplates || [],
            // Use the current service profile for every newly downloaded import template.
            healthcareProviderName: this.healthcareProviderInfo.name,
            healthcareProviderLogoUrl: this.healthcareProviderInfo.logoUrl,
            speechNeeds: this.speechNeeds,
            conditions: this.conditions,
            assessmentDefinitions: this.assessmentDefinitions,
            selectedClientAssessmentResponses: this.selectedClientAssessmentResponses,
            isLoadingSelectedClientAssessments: this.isLoadingSelectedClientAssessments,
            selectedClient: this.selectedClient,
            showFamilySponsorship: this.healthcareProviderInfo.commercialModel === 'self_service',
            familySponsorshipStatus: this.selectedClientSponsorship,
            isLoadingFamilySponsorship: this.isLoadingSelectedClientSponsorship,
            familySponsorshipError: this.selectedClientSponsorshipError,
            canManageFamilySponsorship: this.canManageFamilySponsorship(),
            therapySessions: this.therapySessions,
            therapySessionReports: this.therapySessionReports,
            timelineDocuments: this.timelineDocuments,
            canUseAudioScribeAndReports,
            canUseClientPlans: this.canUseClientPlans(),
            // Only root dashboard users see detailed briefing diagnostics, regardless of legacy user type.
            canViewBriefingDiagnostics: (
                this.authenticatedUser?.dashboardRole || getDashboardRole(this.currentTherapist)
            ) === 'root',
            onSubscribeClientBriefing: (clientId: string, onChange: (state: ClientBriefingState) => void) => {
                this.requireAudioScribeAndReportsAccess();
                return this.app.getData().subscribeClientBriefing(clientId, onChange);
            },
            onSubscribeClientContent: (clientId: string, onChange: (state: ClientContentState) => void) => {
                this.requireClientPlansAccess();
                return this.app.getData().subscribeClientContent(clientId, onChange);
            },
            // Repeat live feature checks when opening or submitting the plan editor.
            onLoadClientPlanCatalogue: (clientId: string) => {
                this.requireClientPlansAccess();
                return this.app.getData().loadClientPlanCatalogue(clientId);
            },
            onSubmitClientPlanOverride: (clientId: string, goalIds: string[]) => {
                this.requireClientPlansAccess();
                return this.app.getData().submitClientPlanOverride(clientId, goalIds);
            },
            onGenerateClientPlan: (clientId: string, instructions: string) => {
                this.requireClientPlansAccess();
                return this.app.getData().generateClientPlan(clientId, instructions);
            },
            onRequestClientBriefing: async (clientId: string, force?: boolean) => {
                this.requireAudioScribeAndReportsAccess();
                return this.app.getData().requestClientBriefing(clientId, force);
            },
            onImportSessionFile: async (
                client: SessionRecordingClient,
                file: File,
                options?: TimelineFileUploadOptions
            ) => {
                this.requireAudioScribeAndReportsAccess();
                return this.onImportSessionFile(client, file, options);
            },
            onSaveSessionTitle: async (sessionId: string, title: string) => {
                this.requireAudioScribeAndReportsAccess();
                await this.app.getData().updateTherapySessionTitle(sessionId, title);
            },
            onSaveSessionDate: async (sessionId: string, date: Date) => {
                this.requireAudioScribeAndReportsAccess();
                await this.app.getData().updateTherapySessionDate(sessionId, date);
            },
            onSaveSessionClinicalNotes: async (sessionId: string, clinicalNotes: string) => {
                this.requireAudioScribeAndReportsAccess();
                await this.app.getData().updateTherapySessionClinicalNotes(sessionId, clinicalNotes);
            },
            onSaveSessionTranscriptUtterances: async (sessionId: string, utterances: Array<TherapySessionTranscriptUtterance>) => {
                this.requireAudioScribeAndReportsAccess();
                await this.app.getData().updateTherapySessionTranscriptUtterances(sessionId, utterances);
            },
            onSaveSessionPlacedTags: async (
                sessionId: string,
                placedTags: Array<TherapySessionPlacedTag>,
                previousPlacedTags?: Array<TherapySessionPlacedTag>
            ) => {
                this.requireAudioScribeAndReportsAccess();
                await this.app.getData().updateTherapySessionPlacedTags(sessionId, placedTags, previousPlacedTags);
            },
            onSaveTherapistSessionTags: async (therapistId: string, tags: Array<TherapySessionTagDefinition>) => {
                this.requireAudioScribeAndReportsAccess();
                await this.app.getData().updateTherapistSessionTags(therapistId, tags);
            },
            onEnsureSessionWaveform: async (sessionId: string) => {
                this.requireAudioScribeAndReportsAccess();
                await this.app.getData().ensureTherapySessionWaveform(sessionId);
            },
            onRetrySessionTranscription: async (sessionId: string) => {
                this.requireAudioScribeAndReportsAccess();
                await this.app.getData().retryTherapySessionTranscription(sessionId);
            },
            onGenerateSessionReport: async (request: TherapySessionReportGenerationRequest) => {
                this.requireAudioScribeAndReportsAccess();
                return await this.app.getData().generateTherapySessionReport(request);
            },
            // Review and correct the file evidence reused by subsequent reports.
            onGetReportAttachmentContent: async (clientId: string, documentId: string) => {
                this.requireAudioScribeAndReportsAccess();
                return this.app.getData().getReportAttachmentContent(clientId, documentId);
            },
            onSaveReportAttachmentContent: async (clientId: string, documentId: string, sourceVersion: string, text: string) => {
                this.requireAudioScribeAndReportsAccess();
                await this.app.getData().saveReportAttachmentContent(clientId, documentId, sourceVersion, text);
            },
            onSaveSessionReportText: async (reportId: string, reportText: string) => {
                this.requireAudioScribeAndReportsAccess();
                await this.app.getData().updateTherapySessionReportText(reportId, reportText);
            },
            onDeleteSessionReport: async (report: TherapySessionReport) => {
                this.requireAudioScribeAndReportsAccess();
                await this.app.getData().deleteTherapySessionReport(report);
            },
            onDeleteSession: async (session: TherapySession) => {
                this.requireAudioScribeAndReportsAccess();
                await this.app.getData().deleteTherapySession(session);
            },
            onCreateTimelineClinicalNote: async (document: Omit<TimelineDocument, 'id' | 'kind'>) => {
                this.requireClientsAccess();
                return await this.app.getData().addTimelineClinicalNote(document);
            },
            onSaveTimelineClinicalNote: async (documentId: string, clinicalNotes: string) => {
                this.requireClientsAccess();
                await this.app.getData().updateTimelineClinicalNote(documentId, clinicalNotes);
            },
            onSaveTimelineDocumentTitle: async (documentId: string, title: string) => {
                this.requireClientsAccess();
                await this.app.getData().updateTimelineDocumentTitle(documentId, title);
            },
            onSaveTimelineDocumentDate: async (documentId: string, date: Date) => {
                this.requireClientsAccess();
                await this.app.getData().updateTimelineDocumentDate(documentId, date);
            },
            onUploadTimelineFile: async (
                document: Omit<TimelineDocument, 'id' | 'kind' | 'storagePath' | 'originalName' | 'contentType' | 'sizeBytes'>,
                file: File,
                options?: TimelineFileUploadOptions
            ) => {
                this.requireClientsAccess();
                return await this.app.getData().addTimelineFileDocument(document, file, options);
            },
            onGetTimelineFileDownloadUrl: async (document: TimelineDocument) => {
                this.requireClientsAccess();
                return await this.app.getData().getTimelineFileDownloadUrl(document);
            },
            onDownloadTimelineFile: async (document: TimelineDocument) => {
                this.requireClientsAccess();
                await this.app.getData().downloadTimelineFileDocument(document);
            },
            onDeleteTimelineDocument: async (document: TimelineDocument) => {
                this.requireClientsAccess();
                await this.app.getData().deleteTimelineDocument(document);
            },
            onSelectClient: (client: Client) => { this.setSelectedClient(client) },
            onClientRecordChange: this.onClientRecordChange,
            onSetFamilySponsorship: async (sponsored: boolean) => {
                await this.setSelectedClientSponsorship(sponsored);
            },
            onAddClient: (client: Client, carers: Client[]) => { this.addClient(client, carers) },
            onImportClients: async (entries: ClientsImportEntry[]) => this.importClients(entries),
            onUpdateClient: (client: Client) => { this.updateClient(client) },
            onDeleteClient: (client: Client) => { this.deleteClient(client) },
        };
    }

    private updateClient(client: Client) {
        this.requireClientsAccess();
        this.app.getData().updateClient(client).then(() => {
            // update the client in the local list
            const index = this.clients.findIndex(c => c.id === client.id);
            if (index !== -1) {
                this.clients[index] = client;
            }
            const allClientsIndex = this.allClients.findIndex(c => c.id === client.id);
            if (allClientsIndex !== -1) {
                this.allClients[allClientsIndex] = client;
            }
            if (this.selectedClient?.id === client.id) {
                this.selectedClient = client;
            }
            this.safeUpdatePresentedData();
        });
    }

    private deleteClient(client: Client) {
        this.requireClientsAccess();
        this.app.getData().deleteClient(client).then(() => {
            this.clients = this.clients.filter(c => c.id !== client.id);
            this.allClients = this.allClients.filter(c => c.id !== client.id);
            this.selectedClient = null;
            this.safeUpdatePresentedData();
        });
    }

    viewWillUpdate(view: PresentedView): void {
        this.view = view;
    }

    viewWillNotUpdate(/*view: PresentedView*/) {
        this.view = null;
    }

    private safeUpdatePresentedData() {
        const data = this.presentedData();
        if (this.view) {
            this.view.updatePresentedData(data);
        }
    }
}
